@import "tailwindcss";
@import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,300;0,14..32,400;0,14..32,500;0,14..32,600;0,14..32,700;0,14..32,800;0,14..32,900&display=swap');

/* ── Design tokens ─────────────────────────────────────────── */
:root {
  /* Layout */
  --header-h:        3.5rem;

  /* Backgrounds — dark navy */
  --bg:              #0d1117;
  --sidebar-bg:      #0f1623;
  --surface:         rgba(22, 27, 42, 0.95);
  --surface-raised:  rgba(28, 35, 52, 0.97);
  --surface-overlay: rgba(13, 17, 28, 0.99);
  --header:          rgba(13, 17, 23, 0.90);

  /* Borders */
  --border:          rgba(255, 255, 255, 0.09);
  --border-strong:   rgba(255, 255, 255, 0.16);

  /* Text */
  --text:            #fafafa;
  --text-2:          #a1a1aa;
  --text-3:          #52525b;

  /* Inputs */
  --input-bg:        rgba(24, 24, 27, 0.90);
  --input-border:    rgba(255, 255, 255, 0.10);

  /* Brand — teal: precision/logistics feel, distinct from all semantic colors */
  --primary:         #14b8a6;
  --primary-dark:    #0d9488;
  --primary-glow:    rgba(20, 184, 166, 0.22);

  /* Semantic */
  --success:         #22c55e;
  --success-bg:      rgba(34, 197, 94, 0.11);
  --success-border:  rgba(34, 197, 94, 0.24);
  --warning:         #f59e0b;
  --warning-bg:      rgba(245, 158, 11, 0.11);
  --warning-border:  rgba(245, 158, 11, 0.24);
  --danger:          #f43f5e;
  --danger-bg:       rgba(244, 63, 94, 0.11);
  --danger-border:   rgba(244, 63, 94, 0.24);
}

.light {
  --bg:              #fafafa;
  --surface:         rgba(255, 255, 255, 0.92);
  --surface-raised:  rgba(255, 255, 255, 0.98);
  --surface-overlay: rgba(255, 255, 255, 0.99);
  --header:          rgba(250, 250, 250, 0.85);
  --border:          rgba(9, 9, 11, 0.08);
  --border-strong:   rgba(9, 9, 11, 0.14);
  --text:            #09090b;
  --text-2:          #52525b;
  --text-3:          #a1a1aa;
  --input-bg:        rgba(244, 244, 245, 0.90);
  --input-border:    rgba(9, 9, 11, 0.11);
  --primary-glow:    rgba(20, 184, 166, 0.15);
  --success-bg:      rgba(34, 197, 94, 0.08);
  --warning-bg:      rgba(245, 158, 11, 0.08);
  --danger-bg:       rgba(244, 63, 94, 0.08);
}

/* ── Base ───────────────────────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

body {
  background-color: var(--bg);
  color: var(--text);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: background-color 0.25s ease, color 0.25s ease;
  min-height: 100dvh;
}

/* ── Custom scrollbar ───────────────────────────────────────── */
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 99px; }
::-webkit-scrollbar-thumb:hover { background: var(--text-3); }

/* ── Glass surface ──────────────────────────────────────────── */
.glass {
  background: var(--surface);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  border: 1px solid var(--border);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.45);
  transition: background 0.2s, border-color 0.2s;
}

.glass-raised {
  background: var(--surface-raised);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid var(--border-strong);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.55);
}

/* ── Input ──────────────────────────────────────────────────── */
.field {
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text);
  border-radius: 0.625rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.field:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-glow);
  outline: none;
}
.field::placeholder { color: var(--text-3); }

/* ── Primary button — dark text required for teal background ── */
.btn-primary {
  background: linear-gradient(135deg, #14b8a6, #0891b2);
  color: #09090b;
  font-weight: 700;
  border-radius: 0.75rem;
  border: none;
  transition: opacity 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 4px 20px var(--primary-glow);
}
.btn-primary:hover  { opacity: 0.90; box-shadow: 0 6px 28px var(--primary-glow); }
.btn-primary:active { transform: scale(0.97); }

/* ── Ghost button ───────────────────────────────────────────── */
.btn-ghost {
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text-2);
  border-radius: 0.75rem;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.btn-ghost:hover { background: var(--surface-raised); color: var(--text); border-color: var(--border-strong); }

/* ── Status badges ──────────────────────────────────────────── */
.badge-success {
  background: var(--success-bg);
  border: 1px solid var(--success-border);
  color: var(--success);
}
.badge-warning {
  background: var(--warning-bg);
  border: 1px solid var(--warning-border);
  color: var(--warning);
}
.badge-danger {
  background: var(--danger-bg);
  border: 1px solid var(--danger-border);
  color: var(--danger);
}

/* ── Stock bar ──────────────────────────────────────────────── */
.stock-bar-healthy { background: var(--success); }
.stock-bar-low     { background: var(--warning); }
.stock-bar-empty   { background: var(--danger); }

/* ── Sidebar nav item ───────────────────────────────────────── */
.nav-item {
  border-radius: 0.625rem;
  transition: background 0.15s, color 0.15s;
  color: var(--text-2);
}
.nav-item:hover { background: var(--surface-raised); color: var(--text); }
.nav-item.active {
  background: var(--surface-raised);
  color: var(--primary);
}

/* ── Misc ───────────────────────────────────────────────────── */
.divider { border-color: var(--border); }

/* Legacy compat */
.glass-panel { background: var(--surface); backdrop-filter: blur(16px) saturate(160%); -webkit-backdrop-filter: blur(16px) saturate(160%); border: 1px solid var(--border); box-shadow: 0 2px 16px rgba(0,0,0,0.45); transition: background 0.2s, border-color 0.2s; }
.glass-input  { background: var(--input-bg); border: 1px solid var(--input-border); color: var(--text); transition: border-color 0.15s, box-shadow 0.15s; }
.glass-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-glow); outline: none; }
